import Dexie, { Table } from 'dexie';

export interface LogEntry {
    id?: number;
    item: string;
    area: string;
    status: string;
    timestamp: string;
    code: string;
    targetClientEmail?: string | null;
    value: string;
    notes: string;
    remoteId?: string | null;
}

export interface UserEntry {
     id: number;
     email: string;
     role: string;
     sheetUrl?: string;
     token?: string;
     networkStatus?: string;
     billingStatus?: string;
     currentProgram?: string;
     accessTags?: string[];
     path?: 'referral' | 'own-product' | null;
 }

export interface TabDataEntry {
    id?: number;
    tabName: string;
    data: any;
    lastSync: string;
}

export interface PendingWorkflowEntry {
    id?: number;
    externalId: string | null;
    mode: string;
    payload: any;
    status: string;
    timestamp: string;
    notes: string;
}

export interface SyncLogEntry {
    id?: number;
    entityType: string;
    entityId: string | null;
    operation: string;
    payload: string;
    timestamp: string;
    status: string;
}

export interface OfflineEventEntry {
    eventId: string;
    timestamp: string;
    operation: string;
    targetId: string;
    targetEntity: string;
    payload: any;
    baseVersion: number;
    status: 'pending' | 'synced' | 'failed' | 'drifted';
}

export class BizBuildingDB extends Dexie {
    logs!: Table<LogEntry, number>;
    user!: Table<UserEntry, number>;
    tabData!: Table<TabDataEntry, number>;
    chat_logs!: Table<any, number>;
    products!: Table<any, number>;
    businessDetails!: Table<any, number>;
    pendingWorkflows!: Table<PendingWorkflowEntry, number>;
    sync_log!: Table<SyncLogEntry, number>;
    leads!: Table<any, number>;
    tasks!: Table<any, number>;
    billing!: Table<any, number>;
    offline_event_log!: Table<OfflineEventEntry, string>;
}

export const db = new BizBuildingDB('BizBuildingClientDB');

db.version(12).stores({
     logs: '++id, item, area, status, timestamp, code, targetClientEmail',
     user: '++id, email, role, sheetUrl, token, networkStatus, billingStatus, currentProgram, path',
     tabData: '++id, tabName, data, lastSync',
     chat_logs: '++id, messageId, threadId, sender, recipient, timestamp, readStatus',
     products: '++id, name, price, sku',
    businessDetails: '++id, companyName, taxNumber',
    pendingWorkflows: '++id, externalId, mode, payload, status, timestamp, notes',
    sync_log: '++id, entityType, entityId, operation, payload, timestamp, status',
    leads: '++id, remoteId, data, lastUpdated, isDirty',
    tasks: '++id, remoteId, data, lastUpdated, isDirty',
    billing: '++id, remoteId, data, lastUpdated, isDirty',
    offline_event_log: 'eventId, timestamp, status, targetEntity, targetId'
});

// ─── Offline Event Log Service ──────────────────────────────────────────────
import { offlineEventLog } from '../services/offlineEventLog';
export { offlineEventLog };

export const addLog = async (area: string, item: string, value: string, notes: string, code: string, remoteId: string | null = null, customTimestamp: string | null = null, targetClientEmail: string | null = null) => {
    return await db.logs.add({
        area,
        item,
        code,
        value,
        notes,
        remoteId,
        targetClientEmail,
        timestamp: customTimestamp || new Date().toISOString(),
        status: 'pending'
    });
};

export const updateLog = async (id: number, newValue: string, newNotes: string, targetClientEmail: string | null = null) => {
    const updates: Partial<LogEntry> = {
        value: newValue,
        notes: newNotes,
        status: 'pending'
    };
    if (targetClientEmail) {updates.targetClientEmail = targetClientEmail;}
    return await db.logs.update(id, updates);
};
